<template>
  <div class="admin-funcionarios">
    <!-- Cabeçalho -->
    <header class="header">
      <h1 class="titulo">👥 Lista de Funcionários</h1>
    </header>

    <!-- Filtros -->
    <div class="filtro-box">
      <input
        v-model="busca"
        type="text"
        placeholder="Buscar por nome ou e-mail..."
        class="input-busca"
      />

      <select v-model="filtroTipo" class="input-select filtro-select">
        <option value="">Todos</option>
        <option value="admin">Administradores</option>
        <option value="nao-admin">Não Admin</option>
      </select>

      <button @click="$router.push({ name: 'CadastrarFuncionario' })" class="btn-principal">
        <i class="fas fa-plus"></i> Novo Funcionário
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Carregando registros...</p>
    </div>

    <!-- Nenhum registro -->
    <div v-else-if="funcionariosFiltrados.length === 0" class="empty-state">
      <i class="fas fa-user-slash empty-icon"></i>
      <h3>Nenhum funcionário encontrado</h3>
      <p>Verifique o filtro ou tente outro termo de busca.</p>
    </div>

    <!-- Lista de funcionários -->
    <div v-else class="funcionarios-list">
      <div v-for="registro in funcionariosFiltrados" :key="registro.id" class="funcionario-card">
        <div class="card-header">
          <h3 class="nome">{{ registro.id_funcionario }} - {{ registro.nome }}</h3>
          <span class="tag-admin" v-if="registro.is_admin">Admin</span>
        </div>

        <div class="card-body">
          <p><i class="fas fa-envelope"></i> {{ registro.email }}</p>
          <p><i class="fas fa-user-tag"></i> {{ registro.cargo || 'Não atribuído' }}</p>
        </div>

        <div class="card-footer">
          <button class="btn-acao" @click="$router.push({ name: 'FuncionarioPerfil', params: { id: registro.id_funcionario } })">
            <i class="fas fa-user-tag"></i> informações
          </button>
          <button class="btn-deletar" @click="deletarFuncionario(registro.id)">
            <i class="fas fa-trash"></i> Excluir
          </button>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <transition name="fade">
      <div v-if="mostrarModal" class="modal-overlay" @click.self="fecharModal">
        <div class="modal">
          <button class="modal-close" @click="fecharModal">✕</button>
          <h3 class="modal-title">
            Atribuir Localização<br />
            <small>para {{ funcionarioSelecionado?.nome }}</small>
          </h3>

          <div v-if="loadingLocalizacoes" class="loading-state">
            <div class="spinner"></div>
            <p>Carregando localizações...</p>
          </div>

          <div v-else>
            <select v-model="localizacaoSelecionada" class="input-select">
              <option disabled value="">Selecione uma localização</option>
              <option v-for="loc in localizacoes" :key="loc.id" :value="loc.id">
                {{ loc.nome }}
              </option>
            </select>

            <div class="modal-actions">
              <button class="btn-secundario" @click="fecharModal">Cancelar</button>
              <button
                class="btn-principal"
                @click="atribuirLocalizacao"
                :disabled="!localizacaoSelecionada || loadingAtribuir"
              >
                <i class="fas fa-check"></i>
                {{ loadingAtribuir ? 'Salvando...' : 'Salvar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { adminService } from '@/services/adminService';

export default {
  name: 'FuncionariosList',
  data() {
    return {
      loading: false,
      registros: [],
      busca: '',
      filtroTipo: '',
      mostrarModal: false,
      funcionarioSelecionado: null,
      localizacoes: [],
      localizacaoSelecionada: '',
      loadingLocalizacoes: false,
      loadingAtribuir: false
    };
  },
  computed: {
    funcionariosFiltrados() {
      let lista = this.registros;

      // Filtro de busca
      if (this.busca.trim()) {
        const termo = this.busca.toLowerCase();
        lista = lista.filter(
          f =>
            f.nome.toLowerCase().includes(termo) ||
            f.email.toLowerCase().includes(termo)
        );
      }

      // Filtro por tipo
      if (this.filtroTipo === 'admin') lista = lista.filter(f => f.is_admin);
      else if (this.filtroTipo === 'nao-admin') lista = lista.filter(f => !f.is_admin);

      return lista;
    }
  },
  methods: {
    async buscarRegistros() {
      this.loading = true;
      try {
        const response = await adminService.listFuncionarios();
        this.registros = response.data.sort((a, b) => a.id - b.id);
        // console.log(this.registros)
      } catch (error) {
        console.error('Erro ao buscar registros:', error);
        alert(error.response?.data?.detail || 'Erro ao buscar registros');
      } finally {
        this.loading = false;
      }
    },

    async abrirModalLocalizacao(funcionario) {
      this.funcionarioSelecionado = funcionario;
      this.mostrarModal = true;
      this.localizacaoSelecionada = '';
      await this.buscarLocalizacoes();
    },

    fecharModal() {
      this.mostrarModal = false;
      this.funcionarioSelecionado = null;
    },

    async buscarLocalizacoes() {
      this.loadingLocalizacoes = true;
      try {
        const response = await adminService.listLocalizacoes();
        this.localizacoes = response.data;
      } catch (error) {
        console.error('Erro ao buscar localizações:', error);
        alert('Erro ao carregar localizações');
      } finally {
        this.loadingLocalizacoes = false;
      }
    },

    async atribuirLocalizacao() {
      if (!this.localizacaoSelecionada) return;
      this.loadingAtribuir = true;
      try {
        const payload = {
          usuario_id: this.funcionarioSelecionado.id,
          localizacao_id: this.localizacaoSelecionada,
          date: new Date().toISOString()
        };
        await adminService.atribuirLocalizacao(payload);
        alert('Localização atribuída com sucesso!');
        this.fecharModal();
        this.buscarRegistros();
      } catch (error) {
        console.error('Erro ao atribuir localização:', error);
        alert(error.response?.data?.detail || 'Erro ao salvar atribuição');
      } finally {
        this.loadingAtribuir = false;
      }
    },

    async deletarFuncionario(id) {
      const confirmacao = confirm('Tem certeza que deseja excluir este funcionário?');
      if (!confirmacao) return;
      try {
        await adminService.deletarFuncionario(id);
        alert('Funcionário excluído com sucesso!');
        this.buscarRegistros();
      } catch (error) {
        console.error('Erro ao excluir funcionário:', error);
        alert(error.response?.data?.detail || 'Erro ao excluir funcionário');
      }
    }
  },
  mounted() {
    this.buscarRegistros();
  }
};
</script>

<style scoped>
/* Estrutura geral */
.admin-funcionarios {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
  background: #f8fafc;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}

/* Cabeçalho */
.header { text-align: center; margin-bottom: 1rem; }
.titulo { font-size: 1.5rem; font-weight: 700; color: #1e293b; }

/* Filtros */
.filtro-box {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
  margin-bottom: 1.2rem;
}
.input-busca {
  flex: 1;
  min-width: 180px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  font-size: 1rem;
}
.filtro-select {
  width: 180px;
}
.input-select {
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  font-size: 1rem;
  color: #334155;
}
.btn-principal {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.btn-principal:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(59,130,246,0.4);
}

/* Cards */
.funcionarios-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.funcionario-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 1rem 1.2rem;
  display: flex;
  flex-direction: column;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
}
.nome {
  font-weight: 700;
  color: #1e293b;
}
.tag-admin {
  background: #fef9c3;
  color: #854d0e;
  font-weight: 600;
  font-size: 0.8rem;
  border-radius: 8px;
  padding: 0.3rem 0.6rem;
}
.card-body { color: #334155; font-size: 0.9rem; }
.card-body p { margin: 0.2rem 0; }
.card-body i { color: #64748b; margin-right: 0.4rem; }
.card-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.8rem;
}

/* Botões */
.btn-acao {
  background: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 0.8rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
}
.btn-acao:hover { background: #059669; }
.btn-deletar {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem 0.8rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
}
.btn-deletar:hover { background: #dc2626; }
.btn-secundario {
  background-color: #e2e8f0;
  color: #1e293b;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-secundario:hover { background: #cbd5e1; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  position: relative;
  display: flex;               /* ✅ ativa flexbox */
  flex-direction: column;      /* ✅ empilha os elementos verticalmente */
  align-items: center;         /* centraliza conteúdo */
  gap: 1.2rem;                 /* espaçamento vertical entre blocos */
  animation: scaleIn 0.25s ease;
}

/* Título e subtítulo */
.modal-title {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
}
.modal-title small {
  display: block;
  font-size: 0.9rem;
  color: #475569;
  font-weight: 500;
  margin-top: 0.25rem;
}

/* Botão fechar */
.modal-close {
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #64748b;
}
.modal-close:hover { color: #1e293b; }

/* Select (input-select) */
.input-select {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #fff;
  color: #334155;
  transition: border-color 0.2s ease;
}
.input-select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

/* Ações */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: 0.6rem;
  margin-top: 0.5rem;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem 0;
}
.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsivo */
@media (max-width: 480px) {
  .modal-actions {
    flex-direction: column;
  }
  .modal-actions button {
    width: 100%;
  }
}


/* Empty & loading */
.loading-state { text-align: center; padding: 2rem; color: #475569; }
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  margin: 0 auto 1rem;
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.empty-state {
  text-align: center;
  color: #64748b;
  padding: 2rem 1rem;
}
.empty-icon {
  font-size: 2.5rem;
  color: #cbd5e1;
  margin-bottom: 0.8rem;
}

/* Animações */
.fade-enter-active, .fade-leave-active { transition: all 0.25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(10px); }
@keyframes scaleIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
</style>
